import React, { useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import cx from "classnames";
import { Button, Icon, Input } from "@showitinc/showit-wc";

import { FORM_FIELD_OPTION_ITEM } from "@/constants/DnDConstants";
import {
  generateDragSpec,
  generateDropSpec,
  getDropIndexOffset,
  getTargetArea,
} from "@/utils/DnDUtils";
import styles from "./FormFieldProperties.module.css";

/**
 * One row of a dropdown or radio set's options list. It is dragged by its handle and dropped on
 * another row to reorder.
 * @param {Object} props
 * @param {number} props.index - The option's position in the list.
 * @param {string} props.label - The option's text.
 * @param {boolean} props.disabled - Blocks editing, removing and dragging.
 * @param {Function} props.onRename - Called with the input's change event.
 * @param {Function} props.onRemove - Called when the remove button is clicked.
 * @param {(fromIndex: number, toIndex: number) => void} props.onMove - Called on drop with the
 *   dragged option's index and the index it should end up at.
 * @returns {JSX.Element}
 */
const FormFieldOptionRow = ({ index, label, disabled, onRename, onRemove, onMove }) => {
  const [dropArea, setDropArea] = useState(null);
  const rowRef = useRef();

  const [{ isDragging }, handleRef, previewRef] = useDrag(
    generateDragSpec({
      type: FORM_FIELD_OPTION_ITEM,
      item: { index },
      canDrag: () => !disabled,
      onDrop: ({ data }) => onMove(data.item.index, data.target.index),
    }),
  );

  const [{ isOver }, dropRef] = useDrop(
    generateDropSpec({
      type: FORM_FIELD_OPTION_ITEM,
      canDrop: (item) => !disabled && item.index !== index,
      onHover: (monitor) => {
        const area = getTargetArea(monitor, rowRef.current);
        if (area !== dropArea) setDropArea(area);
      },
      onDrop: (item, monitor) => {
        const area = getTargetArea(monitor, rowRef.current);
        const offset = getDropIndexOffset({ area, dragIndex: item.index, dropIndex: index });
        return { data: { item, target: { index: index + offset } } };
      },
    }),
  );

  const setRowRef = (node) => {
    rowRef.current = node;
    previewRef(node);
    dropRef(node);
  };

  return (
    <div
      ref={setRowRef}
      className={cx(styles.optionRow, {
        [styles[`drag-${dropArea}`]]: isOver && !isDragging && dropArea,
        [styles.optionDragging]: isDragging,
      })}
      data-testid={`form-field-option-row-${index}`}
    >
      <span
        ref={handleRef}
        className={styles.optionHandle}
        data-testid={`form-field-option-handle-${index}`}
      >
        <Icon type="dragHandle" size="12" />
      </span>
      <Input
        className={styles.optionInput}
        hideLabel
        value={label}
        onSIChange={onRename}
        disabled={disabled}
        data-testid={`form-field-option-${index}`}
      />
      <Button
        variant="minimal"
        iconPrefix="close"
        iconSize="16"
        onClick={onRemove}
        disabled={disabled}
        className={styles.removeOptionButton}
        data-testid={`form-field-remove-option-${index}`}
      />
    </div>
  );
};

export default FormFieldOptionRow;
